import * as Sentry from '@sentry/react'
import { api, RoadieApiResponse, RoadieApiError } from 'roadie-ui'
import { apiGet, ApiResponse } from './apiFactory'
import {
  PaymentTransactionsResponseSchema,
  deserializePaymentTransactionData,
  PaymentTransactions,
} from 'data/transactions'
import {
  LegalAgreementsResponseSchema,
  deserializeLegalAgreementsData,
  LegalAgreementsData,
  LegalAgreementSubmission,
} from 'data/agreements'
import { DrivingSummaryResponseSchema, deserializeDrivingSummaryData, DrivingSummaryData } from 'data/drivingSummary'
import {
  SenderRecognitionsResponseSchema,
  deserializeSenderRecognitionsData,
  SenderRecognitionsData,
} from 'data/senderRecognitions'
import { DriverCertificationsResponseSchema, DriverCertificationsResponse } from 'data/driverCertifications'
import { is4xxError } from './utils'

/**
 * Main API Interface
 *
 * This file centralizes all API calls to the Main API service.
 * Functions are organized by HTTP method (GET, POST, PUT, PATCH, DELETE) and
 * follow RESTful naming conventions:
 * - get*: Retrieve data from the server
 * - create*: Create new resources
 * - update*: Update existing resources (PUT for full updates, PATCH for partial)
 * - delete*: Remove resources
 */

// GET Operations
export function getAuthenticityVerification() {
  return api.get('my_profile/authenticity_verification')
}

export function getBadges() {
  return api.version(2).get('badges')
}

function deserializeDriverCertificationsData(data: unknown): DriverCertificationsResponse {
  return data as DriverCertificationsResponse
}

export async function getDriverCertifications(): Promise<ApiResponse<DriverCertificationsResponse>> {
  return await apiGet(
    api,
    3,
    'my_profile/delivery_certifications_drivers',
    DriverCertificationsResponseSchema,
    deserializeDriverCertificationsData
  )
}

export function getDriverRoute() {
  return api.get('/driver_route')
}

export async function getDrivingSummary(
  startDate?: string,
  endDate?: string
): Promise<ApiResponse<DrivingSummaryData>> {
  const params =
    startDate && endDate
      ? {
          'date_range[start]': startDate,
          'date_range[end]': endDate,
        }
      : undefined

  return await apiGet(
    api,
    3,
    'my_gigs/driving/summary',
    DrivingSummaryResponseSchema,
    deserializeDrivingSummaryData,
    params
  )
}

export function getEarningsStatements() {
  return api.version(3).get('/my_profile/earnings_statements')
}

export function getProfile() {
  return api.get('my_profile')
}

export function getProfileFlags(profile_id: string) {
  return api.get(`profiles/${profile_id}/flags`)
}

export function getProfileSettings(profile_id: string) {
  return api.version(3).get(`profiles/${profile_id}/settings`)
}

export function getTaxSummary(year: string) {
  return api.version(3).get(`my_gigs/driving/tax_summary/${year}`)
}

export async function getTransactions(page: number): Promise<ApiResponse<PaymentTransactions>> {
  return await apiGet(api, 3, '/payouts', PaymentTransactionsResponseSchema, deserializePaymentTransactionData, {
    page,
    per: 5,
  })
}

export async function getConsolidationDetails(cid: string): Promise<RoadieApiResponse | RoadieApiError> {
  try {
    if (cid.match(/gig:\d+/)) {
      // id format is `gig:123`
      return await api.get(`/consolidations/details?ids=${cid}`)
    } else {
      // normal `123` format
      return await api.get(`/consolidations/${cid}/detail`)
    }
  } catch (err: any) {
    return err
  }
}

export async function getConsolidationCompliance(cid: string): Promise<RoadieApiResponse | RoadieApiError> {
  try {
    return await api.version(3).get(`/consolidations/${cid}/compliance`)
  } catch (err: any) {
    return err
  }
}

export async function getGigsDetails(gigIds: string[]): Promise<RoadieApiResponse | RoadieApiError> {
  try {
    return await api.version(3).get(`/gigs/details?ids=${gigIds.join(',')}`)
  } catch (err: any) {
    return err
  }
}

export async function getTermsDocuments(): Promise<ApiResponse<LegalAgreementsData>> {
  return await apiGet(
    api,
    3,
    'my_profile/agreements/terms',
    LegalAgreementsResponseSchema,
    deserializeLegalAgreementsData
  )
}

export async function getDisclosureDocuments(): Promise<ApiResponse<LegalAgreementsData>> {
  return await apiGet(
    api,
    3,
    'my_profile/agreements/disclosures',
    LegalAgreementsResponseSchema,
    deserializeLegalAgreementsData
  )
}

export async function getSenderRecognitions(): Promise<ApiResponse<SenderRecognitionsData>> {
  return await apiGet(
    api,
    3,
    'my_profile/sender_recognitions',
    SenderRecognitionsResponseSchema,
    deserializeSenderRecognitionsData
  )
}

// POST Operations
export function createAddressValidation({
  street,
  apt_number,
  city,
  state,
  zip,
}: {
  street: string
  apt_number: string
  city: string
  state: string
  zip: string
}) {
  return api
    .post('address_validations', {
      street,
      apt_number,
      city,
      state,
      zip,
    })
    .catch(() => {
      return Promise.reject({ data: { errors: { street: 'Invalid Address' } } })
    })
}

export function createBankAccount({
  routing_number,
  account_number,
  account_type,
  account_holder_type,
}: {
  routing_number: string
  account_number: string
  account_type?: string
  account_holder_type?: string
}) {
  return api.post('profile/bank_accounts', {
    bank_account: {
      routing_number,
      account_number,
      account_type,
      account_holder_type,
    },
  })
}

export function createBusinessInfo(business_name: string, state: string, ein: string, multi_member_ein: string) {
  const payload = {
    ein: ein,
    business_name: business_name,
    state_of_incorporation: state,
    multi_member_ein: multi_member_ein,
  }
  return api.version(3).post('ein_verifications', payload)
}

export function createCertification(type: string) {
  return api.post('profile/certifications', { type })
}

export function createCreditCard(payload: Record<string, any>) {
  return api.post('profile/credit_cards', payload)
}

export function createDebitAccount({
  stripe_token,
  last4,
  expiration_month,
  expiration_year,
}: {
  stripe_token: string
  last4: string
  expiration_month: string | number
  expiration_year: string | number
}) {
  return api.version(3).post('profile/debit_accounts', {
    account_token: stripe_token,
    account_last_four: last4,
    expiration_month: expiration_month.toString(),
    expiration_year: expiration_year.toString(),
  })
}

export function createDriverCertification(name: string) {
  return api.version(3).post('my_profile/delivery_certifications_drivers', { name })
}

export function createDriversLicense({
  state,
  number,
  expiration_date,
}: {
  state: string
  number: string
  expiration_date: string
}) {
  return api.version(2).post('drivers_license', { state, number, expiration_date })
}

export function createOnboarding() {
  return api.version(3).post('/onboarding')
}

export function createPhoneConfirmation(code: string) {
  return api.post('user/phone_number_confirmation', { phone_number_verification_code: code })
}

export function createLegalAgreements(documents: LegalAgreementSubmission[]) {
  return api.version(3).post('my_profile/agreements', { documents })
}

// PUT Operations (update/replace resources)
export function updateAvatar(file: File) {
  const data = new FormData()
  data.append('file', file)
  return api.put('my_profile/avatar', data)
}

export function updatePhoneNumber(phone_number: string) {
  return api.put('my_profile/phone_number', { phone_number })
}

export function updatePhoneVerificationCode() {
  return () => {
    return api.put('user/phone_number_confirmation')
  }
}

export function updateProfile(data: Record<string, any>) {
  return api.put('my_profile', data)
}

// PATCH Operations
export function updateBankAccount(
  id: string,
  {
    routing_number,
    account_number,
    account_type,
    account_holder_type,
  }: {
    routing_number: string
    account_number: string
    account_type?: string
    account_holder_type?: string
  }
) {
  return api.patch(`profile/bank_accounts/${id}`, {
    bank_account: {
      routing_number,
      account_number,
      account_type,
      account_holder_type,
    },
  })
}

export function updateBankAccountVerification(id: string, deposits: Record<string, any>) {
  return api.patch(`profile/bank_accounts/${id}/verify`, { deposits })
}

export function updateMarketingCommOptIn(profile_id: string, opt_in: boolean) {
  return api.version(3).patch(`profiles/${profile_id}/settings`, {
    setting: 'marketing_comm_opt_in',
    value: opt_in,
  })
}

export function updatePrivacySettings(profile_id: number, privacy_settings: Record<string, boolean>) {
  return api.patch(`profiles/${profile_id}/flags`, privacy_settings)
}

// DELETE Operations
export function deleteBusinessInfo(id: string) {
  return api.version(3).delete(`ein_verifications/${id}`)
}

export function deleteDriverCertification(name: string) {
  return api.version(3).delete('my_profile/delivery_certifications_drivers', { params: { name } })
}

// Authentication Operations
export function createUserSession(credentials: Record<string, any>) {
  return api.raw().post('user/session', credentials)
}

export function createUserSessionWithToken(token: string) {
  return api.raw().post('user/session', { token })
}

export function createUser(details: Record<string, any>) {
  return api.raw().post('users', { user: details })
}

export function updatePassword(data: Record<string, any>) {
  return api.put('user/password', data)
}

export function updatePasswordReset(email: string, path: string) {
  return api.put('user/password/reset', { email, path }, { headers: { Accept: 'application/json' } }).catch((error) => {
    const status = error?.response?.rawResponse?.status

    if (!is4xxError(status)) {
      Sentry.captureException(error, {
        level: 'error',
        tags: {
          error_type: 'forgot_password_network_error',
          endpoint: '/user/password/reset',
          component: 'updatePasswordReset',
        },
        extra: {
          hasResponse: !!error?.response,
          status,
          errorMessage: error.message,
          errorCode: error.code,
        },
      })
    }

    // Re-throw so the component can still handle the error
    throw error
  })
}

export function updateMFACode(mfa_guid: string, mfa_code: string) {
  return api.version(3).patch(`session/verifications/${mfa_guid}`, { mfa_code })
}

export function updateMFAMethod(mfa_guid: string, mfa_method: string = 'sms') {
  return api.version(3).patch(`session/verifications/${mfa_guid}`, { mfa_method })
}
